/**
 * Turning an image URL into a data URL that jsPDF and html2canvas can both read.
 *
 * Every export path needs this: html2canvas will not reliably paint an <img>
 * that is still loading or that came from another origin, and jsPDF's addImage
 * takes a data URL, so the logos are resolved to data URLs before either runs.
 *
 * It used to be fetch → blob → FileReader in three private copies, and that
 * chain has a silent failure: `fetch` does NOT throw on a non-OK response, and
 * FileReader turns an EMPTY blob into the bare string "data:". jsPDF answers
 * that with
 *
 *   Supplied Data is not a valid base64-String jsPDF.convertStringToImageData
 *
 * which killed the whole export (2026-09-25), and answers a non-image body — a
 * 404 page, say — with a different unhelpful error. Neither is distinguishable
 * from success at the call site, because a string came back either way.
 *
 * So: fetch first, CHECK what came back, and if it is not an image, paint the
 * URL through an <img> onto a canvas instead. The canvas route needs no fetch at
 * all, which is what makes it a real second chance rather than the same attempt
 * twice.
 */

/** Does this string look like something jsPDF can read as an image? */
export function isImageDataUrl(value: string | null | undefined): boolean {
  return (
    !!value && /^data:image\/[a-z0-9+.-]+;base64,[A-Za-z0-9+/=\s]+$/i.test(value)
  );
}

/** fetch → blob → data URL. */
async function viaFetch(url: string): Promise<string> {
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText || 'error'} for ${url}`);
  }
  const blob = await res.blob();
  if (blob.size === 0) {
    throw new Error(`empty body for ${url}`);
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Paint the image onto a canvas and read it back as a PNG.
 *
 * Re-encodes rather than copying the bytes, so the result is a valid PNG
 * whatever the source was. A cross-origin image with no CORS headers taints the
 * canvas and `toDataURL` throws — which is the right answer, because the caller
 * then falls through to whatever signed-URL path it has.
 */
function viaCanvas(url: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    const sameOrigin =
      !/^https?:\/\//i.test(url) || url.startsWith(`${window.location.origin}/`);
    if (!sameOrigin) img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx || !canvas.width || !canvas.height) {
        reject(new Error(`could not paint ${url} onto a canvas`));
        return;
      }
      try {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error(`could not load ${url}`));
    img.src = url;
  });
}

/**
 * The image at `url` as a data URL, or a throw. Never returns a string that is
 * not an image: that is the whole point of it.
 */
export async function toImageDataUrl(url: string): Promise<string> {
  let fetchFailure = '';
  try {
    const fetched = await viaFetch(url);
    if (isImageDataUrl(fetched)) return fetched;
    fetchFailure = `got ${fetched.slice(0, 40)}… (${fetched.length} chars)`;
  } catch (error) {
    fetchFailure = String((error as any)?.message || error);
  }

  console.warn(
    `[imageDataUrl] fetching ${url} did not give an image (${fetchFailure}); ` +
      'painting it through a canvas instead',
  );
  return await viaCanvas(url);
}
